import {
  Button,
  cn,
  InviteAllowance,
  Label,
  ParsleyMark,
  PillButton,
  ResponsiveDialog,
  ResponsiveDialogHeader,
  SegmentedToggle,
  TextField,
  toast
} from '@parsleyai/ui'
import { Check, Clock, Copy, Link2, Loader2, Mail, Plus, QrCode, Send, Users } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsMobile } from '@/lib/useIsMobile'
import { callMintAndSendInvite, callMintQrInvite } from '@/services/firebase/functions'
import { useProfile } from '@/store'
import InviteQrCode from './InviteQrCode'
import { deriveInviteAllowance } from './inviteQuota'

// Local UI-state unions (mirrors the ThemeValue pattern — not a shared/exported closed set).
type InviteMethod = 'email' | 'qr'
type QrPhase = 'intro' | 'generating' | 'result'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DAY_MS = 24 * 60 * 60 * 1000
// Display-only mirror of the server DEFAULT_INVITE_EXPIRY_DAYS (functions
// generateInvitesArgs.ts). The authoritative expiry is stamped server-side; this only drives
// the "expires in N days" caption. Keep in sync if the server default changes.
const QR_EXPIRY_DAYS = 30
// How long the copied-confirmation (check icon) shows before reverting to the copy icon.
const COPIED_MS = 1400

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// The "Invite a friend" modal: email a beta invite, or switch to the QR path which mints an
// UNASSIGNED code. Quota is consumed on send/mint; the count updates from the profile snapshot.
export default function InviteFriendDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const { capabilities, inviteQuota } = useProfile()
  const isMobile = useIsMobile()
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [method, setMethod] = useState<InviteMethod>('email')
  const [qrPhase, setQrPhase] = useState<QrPhase>('intro')
  const [code, setCode] = useState('')
  const [inviteUrl, setInviteUrl] = useState('')
  const [copied, setCopied] = useState<'code' | 'link' | null>(null)

  const allowance = deriveInviteAllowance(capabilities, inviteQuota, Date.now())
  const exhausted = allowance.remaining <= 0
  const valid = EMAIL_RE.test(email.trim())
  const canSend = valid && !sending && !exhausted

  // The result view is deliberately stripped down (design): the allowance meter and the method
  // toggle both hide so the focus is the code itself.
  const inResult = method === 'qr' && qrPhase === 'result'

  const resetLabel =
    allowance.resetInMs === null
      ? ''
      : (() => {
          const days = Math.max(1, Math.ceil(allowance.resetInMs / DAY_MS))
          return t(days === 1 ? 'invite.resetInDay' : 'invite.resetInDays', { count: days })
        })()

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      // Reset after the close animation finishes.
      setTimeout(() => {
        setEmail('')
        setSending(false)
        setMethod('email')
        setQrPhase('intro')
        setCode('')
        setInviteUrl('')
        setCopied(null)
      }, 200)
    }
  }

  const handleMethod = (next: InviteMethod) => {
    setMethod(next)
    // Never leave the QR path parked on the spinner state when toggling back to it.
    if (next === 'qr' && qrPhase === 'generating') setQrPhase('intro')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSend) return
    const to = email.trim()
    setSending(true)
    try {
      await callMintAndSendInvite(to)
      // The allowance count updates reactively from the profile-doc snapshot; the toast is
      // the confirmation. Clear the field so another can be sent without closing.
      toast.success(t('invite.toast.sent', { email: to }), { id: 'invite' })
      setEmail('')
    } catch (_err) {
      toast.error(t('invite.toast.error'), { id: 'invite' })
    } finally {
      setSending(false)
    }
  }

  const handleGenerate = async () => {
    if (exhausted || qrPhase === 'generating') return
    setQrPhase('generating')
    try {
      const { data } = await callMintQrInvite()
      setCode(data.code)
      setInviteUrl(data.inviteUrl)
      setCopied(null)
      setQrPhase('result')
    } catch (_err) {
      // A gated caller shouldn't hit permission/quota errors, so this is a real failure —
      // return to intro and surface it. No quota is consumed on a failed mint.
      toast.error(t('invite.toast.qrError'), { id: 'invite' })
      setQrPhase('intro')
    }
  }

  const handleCopy = (text: string, which: 'code' | 'link', message: string) => {
    navigator.clipboard?.writeText(text)
    setCopied(which)
    setTimeout(() => setCopied((c) => (c === which ? null : c)), COPIED_MS)
    toast.success(message, { id: 'invite' })
  }

  // Return the result view to the modal's default open state (email selected) rather than
  // minting again in place. Generating another code then takes a deliberate toggle-to-QR +
  // Generate, so the one-tap "new code" link can't be used to spam-burn the quota.
  const resetToStart = () => {
    setMethod('email')
    setQrPhase('intro')
    setCode('')
    setInviteUrl('')
    setCopied(null)
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={handleOpenChange}
      aria-label={t('invite.heading')}
      onOpenAutoFocus={(e) => {
        // Radix focuses the first field on open; on mobile that pops the keyboard and covers
        // the modal before the user sees it. Suppress it there — desktop keeps open-focus.
        if (isMobile) e.preventDefault()
      }}>
      {/* ResponsiveDialog renders children with no inter-child gap (unlike the old
          DialogContent's grid gap-4), so space the body ourselves. */}
      <div className="space-y-4">
        <ResponsiveDialogHeader
          icon={<ParsleyMark filled className="h-7 w-6" />}
          title={t('invite.heading')}
          description={method === 'qr' ? t('invite.descriptionQr') : t('invite.description')}
        />

        {!inResult && <InviteAllowance remaining={allowance.remaining} total={allowance.total} resetLabel={resetLabel} />}

        {!exhausted && !inResult && (
          <SegmentedToggle<InviteMethod>
            className="w-full"
            aria-label={t('invite.method.label')}
            value={method}
            onValueChange={handleMethod}
            options={[
              { value: 'email', label: t('invite.method.email'), icon: <Mail /> },
              { value: 'qr', label: t('invite.method.qr'), icon: <QrCode /> }
            ]}
          />
        )}

        {exhausted ? (
          <div className="flex items-start gap-2.5 rounded-[13px] border border-amber-hairline bg-amber-wash px-4 py-3.5 text-sm leading-relaxed text-amber-strong">
            <Clock className="mt-0.5 h-[18px] w-[18px] shrink-0" />
            <span>{resetLabel ? t('invite.exhaustedReset', { reset: resetLabel }) : t('invite.exhausted')}</span>
          </div>
        ) : method === 'email' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="invite-email" className="mb-1.5">
                {t('invite.email.label')}
              </Label>
              <div className="relative">
                <TextField
                  id="invite-email"
                  type="email"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('invite.email.placeholder')}
                  state={valid ? 'success' : 'default'}
                  inputClassName="pr-9"
                  required
                  // Autofocus on desktop only — on mobile the keyboard would open on launch and
                  // cover the modal before the user sees the field.
                  autoFocus={!isMobile}
                />
                {valid && (
                  <span className="-translate-y-1/2 absolute top-1/2 right-4 flex items-center" aria-hidden>
                    <span className="flex size-[26px] items-center justify-center rounded-full bg-brand-green-fill text-brand-green-on-fill motion-safe:animate-[invite-pop_0.44s_cubic-bezier(0.22,0.68,0,1.4)_both]">
                      <Check className="size-3.5" strokeWidth={3.5} />
                    </span>
                  </span>
                )}
              </div>
            </div>
            <Button type="submit" variant="subtle" size="cta" className="w-full gap-2 font-semibold" disabled={!canSend}>
              {sending ? (
                t('invite.sending')
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {t('invite.send')}
                </>
              )}
            </Button>
          </form>
        ) : qrPhase === 'result' ? (
          <div>
            <InviteQrCode url={inviteUrl} />
            <p className="mb-4 text-center text-sm text-faint">{t('invite.qr.caption')}</p>

            <div className="mb-2.5 flex h-[50px] items-center gap-2 rounded-xl border-[1.5px] border-input bg-card-sunken pr-1.5 pl-4">
              <span className="flex-1 truncate font-mono text-base tracking-[0.02em] text-foreground">{code}</span>
              <button
                type="button"
                onClick={() => handleCopy(code, 'code', t('invite.toast.codeCopied'))}
                aria-label={t('invite.qr.copyCode')}
                className={cn(
                  'flex size-[38px] shrink-0 items-center justify-center rounded-[9px] transition-colors hover:bg-muted',
                  copied === 'code' ? 'text-brand-green-600 dark:text-brand-green-400' : 'text-muted-foreground hover:text-foreground'
                )}>
                {copied === 'code' ? <Check className="size-[17px]" strokeWidth={2.75} /> : <Copy className="size-[17px]" />}
              </button>
            </div>

            <PillButton
              variant="secondary"
              size="cta"
              className="w-full"
              icon={copied === 'link' ? <Check /> : <Link2 />}
              onClick={() => handleCopy(inviteUrl, 'link', t('invite.toast.linkCopied'))}>
              {copied === 'link' ? t('invite.qr.linkCopied') : t('invite.qr.copyLink')}
            </PillButton>

            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-faint">
              <Clock className="h-3.5 w-3.5" />
              {t('invite.qr.expiry', { count: QR_EXPIRY_DAYS })}
            </div>

            <button
              type="button"
              onClick={resetToStart}
              className="mx-auto mt-3.5 flex items-center gap-1.5 text-sm font-semibold text-brand-green-700 transition-colors hover:text-brand-green-900 dark:text-brand-green-400 dark:hover:text-brand-green-300">
              <Plus className="h-[15px] w-[15px]" />
              {t('invite.qr.inviteAnother')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-brand-green-600 dark:text-brand-green-400" />
              <span>{t('invite.qr.hint')}</span>
            </div>
            <Button
              variant="subtle"
              size="cta"
              className="w-full gap-2 font-semibold"
              disabled={qrPhase === 'generating'}
              onClick={handleGenerate}>
              {qrPhase === 'generating' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('invite.qr.minting')}
                </>
              ) : (
                <>
                  <QrCode className="h-4 w-4" />
                  {t('invite.qr.generate')}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </ResponsiveDialog>
  )
}
